import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Settings,
  Logout,
  AdminPanelSettings,
  Home,
  Menu as MenuIcon,
  SmartToy,
  Chat,
  UploadFile
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/DomainContext';

const MainNavbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user, profile, logout } = useAuth() || {};
  const { projectName, brandName, logoUrl, primaryColor } = useBranding() || {};

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);

  const handleUserMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);
  const handleMobileMenuOpen = (event) => setMobileMenuAnchorEl(event.currentTarget);
  const handleMobileMenuClose = () => setMobileMenuAnchorEl(null);

  const handleNavigation = (path) => {
    navigate(path);
    handleUserMenuClose();
    handleMobileMenuClose();
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Errore durante il logout:", error);
      navigate('/');
    }
  };

  const displayName = profile?.user_display_name || user?.displayName || "Utente";
  const userEmail = profile?.user_email || user?.email || "";
  const photoURL = profile?.user_photo_url || user?.photoURL;
  const isAdmin = profile?.roles?.some(role => [
    'platform_admin', 'project_admin', 'brand_admin', 'organization_admin'
  ].includes(role));

  const navbarStyle = {
    background: primaryColor ? `linear-gradient(45deg, ${primaryColor} 30%, ${primaryColor}dd 90%)` : theme.palette.primary.main,
    boxShadow: primaryColor ? `0 3px 5px 2px ${primaryColor}33` : theme.shadows[4]
  };

  return (
    <AppBar position="static" sx={navbarStyle}>
      <Toolbar>
        {/* --- LOGO E BRAND (sempre visibile, porta a '/') --- */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={() => handleNavigation('/')}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{ height: 40, mr: 2, display: { xs: 'none', sm: 'block' } }}
            />
          )}
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
              {projectName || 'Progetto'}
            </Typography>
            {brandName && (
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                {brandName}
              </Typography>
            )}
          </Box>
        </Box>

        {/* ---- DESKTOP NAV ---- */}
        {!isMobile && (
          <>
            {/* --- SOLO PER UTENTI AUTENTICATI --- */}
            {user && (
              <>
                {/* --- PULSANTE HOME --- */}
                <Button color="inherit" startIcon={<Home />} onClick={() => handleNavigation('/')}>
                  Home
                </Button>
                {/* --- PULSANTE RAGIONIAMO --- */}
                <Button color="inherit" startIcon={<SmartToy />} onClick={() => handleNavigation('/user/chat-page')}>
                  Ragioniamo
                </Button>
                {/* --- NUOVO PULSANTE PARLIAMONE --- */}
                <Button color="inherit" startIcon={<Chat />} onClick={() => handleNavigation('/user/agent-chat-page')}>
                  Parliamone
                </Button>
                {/* --- PULSANTE ADMIN (solo per admin) --- */}
                {isAdmin && (
                  <Button color="inherit" startIcon={<AdminPanelSettings />} onClick={() => handleNavigation('/AdminDashboard')}>
                    Admin
                  </Button>
                )}
              </>
            )}
          </>
        )}

        {/* --- UTENTE MENU O PULSANTE LOGIN --- */}
        {user ? (
          <Box>
            <IconButton color="inherit" onClick={handleUserMenuOpen}>
              <Avatar src={photoURL} alt={displayName} sx={{ width: 32, height: 32 }}>
                {displayName ? displayName.charAt(0).toUpperCase() : 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              PaperProps={{ sx: { mt: 1.5, minWidth: 240, borderRadius: 2 } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight="bold">{displayName}</Typography>
                <Typography variant="body2" color="text.secondary">{userEmail}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => handleNavigation('/profile')}>
                <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                <ListItemText>Profilo</ListItemText>
              </MenuItem>
              {/* --- NUOVO: FILE MANAGER NEL MENU UTENTE --- */}
              <MenuItem onClick={() => handleNavigation('/user/file-manager')}>
                <ListItemIcon><UploadFile fontSize="small" /></ListItemIcon>
                <ListItemText>File Manager</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <>
            {!isMobile && (
              <>
                <Button color="inherit" startIcon={<Home />} onClick={() => handleNavigation('/')}>
                  Home
                </Button>
                <Button color="inherit" variant="outlined" onClick={() => handleNavigation('/login')}>
                  Accedi
                </Button>
              </>
            )}
          </>
        )}

        {/* ---- MOBILE NAV ---- */}
        {isMobile && (
          <IconButton color="inherit" onClick={handleMobileMenuOpen}>
            <MenuIcon />
          </IconButton>
        )}
        <Menu
          anchorEl={mobileMenuAnchorEl}
          open={Boolean(mobileMenuAnchorEl)}
          onClose={handleMobileMenuClose}
        >
          {user ? (
            // --- Menu mobile per utenti loggati ---
            [
              // --- VOCE DI MENU HOME ---
              <MenuItem key="home" onClick={() => handleNavigation('/')}>
                <ListItemIcon><Home fontSize="small" /></ListItemIcon>
                <ListItemText>Home</ListItemText>
              </MenuItem>,
              // --- VOCE RAGIONIAMO ---
              <MenuItem key="ragioniamo" onClick={() => handleNavigation('/user/chat-page')}>
                <ListItemIcon><SmartToy fontSize="small" /></ListItemIcon>
                <ListItemText>Ragioniamo</ListItemText>
              </MenuItem>,
              // --- NUOVA VOCE PARLIAMONE ---
              <MenuItem key="parliamone" onClick={() => handleNavigation('/user/chat-page')}>
                <ListItemIcon><Chat fontSize="small" /></ListItemIcon>
                <ListItemText>Parliamone</ListItemText>
              </MenuItem>,
              // --- ADMIN (solo per admin) ---
              isAdmin && (
                <MenuItem key="admin" onClick={() => handleNavigation('/AdminDashboard')}>
                  <ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon>
                  <ListItemText>Admin</ListItemText>
                </MenuItem>
              )
            ]
          ) : (
            // --- Menu mobile per utenti non loggati ---
            [
              <MenuItem key="home-public" onClick={() => handleNavigation('/')}>
                  <ListItemIcon><Home fontSize="small" /></ListItemIcon>
                  <ListItemText>Home</ListItemText>
              </MenuItem>,
              <MenuItem key="login" onClick={() => handleNavigation('/login')}>
                <ListItemText>Accedi</ListItemText>
              </MenuItem>
            ]
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default MainNavbar;